import { PropsOf, useMergeRefs } from "@chakra-ui/react";
import { FocusableElement } from "@chakra-ui/utils";
import {
  CreatableSelect as ChakraReactCreatableSelect,
  GroupBase,
  OnChangeValue,
  Select as ChakraReactSelect,
} from "chakra-react-select";
import { CreatableSelectComponent } from "chakra-react-select/dist/types/select/creatable-select";
import { ChakraStylesConfig } from "chakra-react-select/dist/types/types";
import React, { FunctionComponent, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SelectInstance } from "react-select";
import { SelectComponentsConfig } from "react-select/dist/declarations/src/components";
import { OptionsOrGroups } from "react-select/dist/declarations/src/types";
import { selectComponentsConfig } from "shared/components/input/select/SelectComponents";
import { useSelectJump } from "shared/components/input/select/SelectJump";
import { chakraSelectStyles } from "shared/components/input/select/SelectStyles";
import { SelectOptionData } from "shared/components/input/select/SelectTypes";
import {
  flattenSelectOptions,
  isSelectMultiValue,
  isSelectNewOptionData,
  isSelectOptionData,
} from "shared/components/input/select/SelectUtils";
import { FormInputValue } from "shared/types/FormInputValue";
import { createSyntheticEvent } from "shared/utils/EventUtils";
import { useDeepCompareMemo } from "use-deep-compare";

export interface SelectBaseProps<
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
> {
  name?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isOptional?: boolean;
  isInvalid?: boolean;
  isSearchable?: boolean;
  value?: (IsMulti extends true ? T[] : T) | null;
  onChangeValue?: (value: IsMulti extends true ? T[] : T) => void;
  variant?: "simple" | "simple-table" | "filter";
  isMulti?: IsMulti;
  isCreatable?: boolean;
  onCreateOption?: (inputValue: string) => void;
  createObjectName?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  openMenuOnFocus?: boolean;
}

interface SelectInternalProps<
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
> extends SelectBaseProps<T, IsMulti> {
  options: OptionsOrGroups<SelectOptionData, GroupBase<SelectOptionData>>;
  chakraStyles?: ChakraStylesConfig<
    SelectOptionData,
    IsMulti,
    GroupBase<SelectOptionData>
  >;
  components?: SelectComponentsConfig<
    SelectOptionData,
    IsMulti,
    GroupBase<SelectOptionData>
  >;
}

// This type is needed so the generic type args are not lost when using forwardRef
type SelectInternal = <
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
>(
  props: SelectInternalProps<T, IsMulti> & {
    ref?: React.Ref<
      SelectInstance<SelectOptionData, IsMulti> | FocusableElement
    >;
  },
) => React.ReactElement | null;

// eslint-disable-next-line no-redeclare,react/display-name
export const SelectInternal = React.forwardRef(
  <T extends FormInputValue = string, IsMulti extends boolean = false>(
    {
      isCreatable,
      isDisabled,
      isReadOnly,
      isMulti,
      isOptional,
      isInvalid,
      isSearchable: isSearchableProp,
      value,
      onChangeValue,
      options,
      openMenuOnFocus,
      chakraStyles = chakraSelectStyles,
      components = selectComponentsConfig,
      ...props
    }: SelectInternalProps<T, IsMulti>,
    fwRef: React.Ref<SelectInstance<SelectOptionData, IsMulti>>,
  ): React.ReactElement | null => {
    const { t } = useTranslation(["common"]);

    const localRef = useRef<SelectInstance<SelectOptionData, IsMulti>>(null);
    const ref = useMergeRefs(fwRef, localRef);

    // https://github.com/JedWatson/react-select/issues/4370#issuecomment-1102383076
    const memoizedOptions = useDeepCompareMemo(() => options, [options]);

    const selectedOption = useMemo(() => {
      const flatOptions = flattenSelectOptions(memoizedOptions);
      return (
        (Array.isArray(value)
          ? flatOptions.filter(
              (it) => isSelectOptionData(it) && value.includes(it.value as T),
            )
          : flatOptions.find(
              (it) => isSelectOptionData(it) && it.value === value,
              // NOTE: Must use `null` instead of `undefined` to clear the value, see:
              //  https://github.com/JedWatson/react-select/issues/3712
            )) ?? null
      );
    }, [memoizedOptions, value]);

    const onChangeOption = (
      option: OnChangeValue<SelectOptionData, IsMulti>,
    ) => {
      onChangeValue?.(
        (isSelectMultiValue(option)
          ? option.map((it) => it.value)
          : option?.value) as never,
      );
    };

    const SelectComponent = isCreatable
      ? ChakraReactCreatableSelect<SelectOptionData, IsMulti>
      : ChakraReactSelect<SelectOptionData, IsMulti>;

    const otherProps = (() => {
      if (isCreatable) {
        const { createObjectName, onCreateOption, ...otherProps } = props;
        return {
          ...otherProps,
          onCreateOption,
          isValidNewOption: () => true,
          formatCreateLabel: (inputValue) =>
            inputValue
              ? t("common:createNameValue", {
                  name: createObjectName,
                  value: inputValue,
                })
              : t("common:createName", {
                  name: createObjectName,
                }),
          createOptionPosition: "first",
        } satisfies PropsOf<CreatableSelectComponent>;
      }
      return props;
    })();

    const isSearchable =
      isSearchableProp ?? Boolean(memoizedOptions.length > 10 || isCreatable);

    const scrollToSelectedOption = useCallback(() => {
      const selectElem = localRef.current;
      if (!selectElem) {
        return;
      }
      selectElem.scrollToFocusedOptionOnUpdate = false;
      const selectedOptionElem = selectElem.menuListRef?.querySelector(
        ".react-select__option--is-selected",
      );
      selectedOptionElem?.scrollIntoView({
        inline: "center",
        block: "center",
        behavior: "auto",
      });
    }, []);

    const onKeyDownJump = useSelectJump(
      localRef,
      isSearchable,
      isMulti,
      options,
      value,
      onChangeValue,
    );

    return (
      <SelectComponent
        {...otherProps}
        ref={ref}
        options={memoizedOptions}
        value={selectedOption}
        onChange={onChangeOption}
        isDisabled={isDisabled || isReadOnly}
        openMenuOnFocus={openMenuOnFocus}
        isReadOnly={isReadOnly}
        isClearable={isOptional ?? true}
        isMulti={isMulti}
        isInvalid={isInvalid}
        isSearchable={isSearchable}
        getOptionLabel={(option) => option.label}
        isOptionDisabled={(option) => Boolean(option.isDisabled)}
        getOptionValue={(option) => option.value.toString()}
        filterOption={(option, rawInput) =>
          isSelectNewOptionData(option.data) ||
          option.label.toLowerCase().includes(rawInput.toLowerCase())
        }
        variant="unstyled"
        classNamePrefix="react-select"
        components={components}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        useBasicStyles={true}
        chakraStyles={chakraStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        onMenuOpen={() => {
          setTimeout(() => {
            scrollToSelectedOption();
          });
        }}
        onKeyDown={(e) => {
          onKeyDownJump(e);
          // workaround for https://github.com/JedWatson/react-select/pull/5599
          if (e.key === "Escape") {
            const isMenuOpen = Boolean(localRef.current?.menuListRef);
            if (!isMenuOpen) {
              const newEvent = createSyntheticEvent(
                new KeyboardEvent("keydown", e.nativeEvent),
              );
              (e.currentTarget as HTMLElement).parentElement?.dispatchEvent(
                newEvent.nativeEvent,
              );
            }
            // workaround for https://github.com/JedWatson/react-select/issues/3414#issuecomment-590264726
          } else if (e.key === "Enter") {
            const selectElem = localRef.current;
            if (selectElem && !selectElem.props.menuIsOpen) {
              selectElem.openMenu("first");
            }
          }
        }}
        styles={{
          menuPortal: (provided) => ({ ...provided, zIndex: 100 }),
        }}
      />
    );
  },
) as SelectInternal;
(SelectInternal as FunctionComponent).displayName = "Select";
